/**
 * Artist-ID auto-collection state model — the rules for turning the backend's run
 * report into what the admin sees on the Populate IDs button.
 *
 * Why this exists as its own module: the bug this replaces was a *state* bug, not a
 * rendering one. The old hook folded "never started", "cancelled", "failed", "Dagster
 * unreachable" and "mid-relaunch" into a single `'not-loaded'` that also meant *safe to
 * click again* — so a poll landing in a blind window re-enabled the button, the admin
 * clicked, and the backend cancelled the run that was already collecting. That is the
 * stack of CANCELED runs in Dagster.
 *
 * Kept free of React so the rules are testable without a DOM (see
 * `artistIdCollectionModel.test.ts`) — the repo has no React Testing Library, and these
 * are exactly the rules worth pinning down.
 */

/** Backend lifecycle phase — the source of truth. Mirrors `ArtistIdCollectionRunPhase`. */
export type CollectionPhase =
  | 'LAUNCHING'
  | 'STARTING'
  | 'RUNNING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'CANCELLING'
  | 'CANCELLED'
  | null;

/**
 * Coarse UI status.
 *
 * `unknown` is the important addition: it means "we have not been able to reach the
 * backend", which is emphatically *not* `idle`. The old model had no way to say this, so
 * every transient error read as "no job is running".
 */
export type CollectionStatus =
  | 'idle'
  | 'triggering'
  | 'queued'
  | 'starting'
  | 'collecting'
  | 'cancelling'
  | 'cancelled'
  | 'loaded'
  | 'failed'
  | 'unknown';

/** A run in one of these is genuinely in flight — the trigger must stay disabled. */
export const ACTIVE_COLLECTION_STATUSES: readonly CollectionStatus[] = [
  'triggering',
  'queued',
  'starting',
  'collecting',
  'cancelling',
];

export function isActiveCollectionStatus(status: CollectionStatus): boolean {
  return ACTIVE_COLLECTION_STATUSES.includes(status);
}

/** The subset of the run report this model reads. */
export type CollectionRunReport = {
  phase?: string | null;
  status?: string | null;
  isStalled?: boolean;
  errorDetail?: string | null;
  requestedAt?: Date | null;
  lastProgressAt?: Date | null;
  source?: string | null;
  sourceId?: string | null;
};

/**
 * Map a run report to the coarse status.
 *
 * Phase wins; the legacy DagsterRunStatus is only a fallback for a backend that has not
 * been deployed yet. An *unrecognised* value maps to `unknown`, never to `idle` — the old
 * `default: setStatus('not-loaded')` is precisely what re-enabled the button mid-run.
 * Only a report that explicitly says "no run exists" (both fields absent, or the legacy
 * `NOT_STARTED`) is idle.
 */
export function deriveCollectionStatus(
  report: CollectionRunReport | null | undefined
): CollectionStatus {
  if (!report) return 'unknown';

  switch (report.phase) {
    case 'LAUNCHING':
      return 'queued';
    case 'STARTING':
      return 'starting';
    case 'RUNNING':
      return 'collecting';
    case 'SUCCEEDED':
      return 'loaded';
    case 'FAILED':
      return 'failed';
    case 'CANCELLING':
      return 'cancelling';
    case 'CANCELLED':
      return 'cancelled';
    case null:
    case undefined:
      break;
    default:
      // A phase this build does not know about. Treat it as "we cannot see the backend"
      // rather than falling through to the legacy `status`, which could map a phase we do
      // not understand onto `loaded` and hand the admin a stale, re-enabled button.
      return 'unknown';
  }

  // No phase: either a pre-phase backend, or genuinely no run on record.
  switch (report.status) {
    case 'QUEUED':
    case 'MANAGED':
      return 'queued';
    case 'STARTING':
      return 'starting';
    case 'STARTED':
      return 'collecting';
    case 'SUCCESS':
      return 'loaded';
    case 'FAILURE':
    case 'FAILED':
      return 'failed';
    case 'CANCELING':
      return 'cancelling';
    case 'CANCELED':
      return 'cancelled';
    case 'NOT_STARTED':
      return 'idle';
    case null:
    case undefined:
      // Phase is absent too (checked above), so this really is "no run on record".
      return 'idle';
    default:
      return 'unknown';
  }
}

/**
 * Whether the poll loop should keep running.
 *
 * `unknown` polls too — that is the fix for "one hiccup and the UI never recovers". The
 * loop's job is to *regain* contact, so a failing request is a reason to keep asking, not
 * to stop. Terminal states stop the loop because nothing further will change until the
 * admin acts.
 */
export function shouldPollCollection(status: CollectionStatus): boolean {
  return isActiveCollectionStatus(status) || status === 'unknown';
}

/**
 * Poll delay, in ms. Steady 3s while a run is genuinely in flight; on repeated failures
 * back off (3s, 6s, 12s… capped at 30s) so a backend outage doesn't turn every open admin
 * tab into a load generator, while still recovering on its own once the backend returns.
 */
export const BASE_POLL_INTERVAL_MS = 3000;
export const MAX_POLL_INTERVAL_MS = 30000;

export function nextPollDelayMs(consecutiveFailures: number): number {
  if (consecutiveFailures <= 0) return BASE_POLL_INTERVAL_MS;
  return Math.min(
    BASE_POLL_INTERVAL_MS * 2 ** consecutiveFailures,
    MAX_POLL_INTERVAL_MS
  );
}

/**
 * How many consecutive failed polls before the UI admits it has lost contact.
 * One hiccup must not change what the admin sees; a sustained outage must.
 */
export const UNKNOWN_AFTER_CONSECUTIVE_FAILURES = 3;

/** Whether the trigger button should be disabled for this status. */
export function isTriggerDisabled(status: CollectionStatus): boolean {
  // `unknown` disables too: if we cannot see the backend we cannot promise there is no run
  // in flight, and a click in that window is exactly what caused the reported bug.
  return isActiveCollectionStatus(status) || status === 'unknown';
}

/** Whether a Cancel affordance makes sense. */
export function canCancelCollection(status: CollectionStatus): boolean {
  return (
    status === 'queued' || status === 'starting' || status === 'collecting'
  );
}

/** Elapsed whole seconds since the run was requested; null when there is nothing to time. */
export function elapsedSeconds(
  requestedAt: Date | number | null | undefined,
  now: number
): number | null {
  if (requestedAt === null || requestedAt === undefined) return null;
  const started =
    typeof requestedAt === 'number' ? requestedAt : requestedAt.getTime();
  if (Number.isNaN(started)) return null;
  return Math.max(0, Math.floor((now - started) / 1000));
}

/** `0:42`, `12:05` — a running clock the admin can watch tick. */
export function formatElapsed(totalSeconds: number | null): string | null {
  if (totalSeconds === null) return null;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export type CollectionIndicator = {
  /** Short phase label, e.g. "Collecting IDs". */
  label: string;
  /** Label plus elapsed clock, e.g. "Collecting IDs… 0:42". */
  detail: string;
  /** Something is in flight — render a spinner. */
  busy: boolean;
  /** Needs the admin's attention (failed, stalled, lost contact). */
  attention: boolean;
};

/**
 * The single place that decides what the admin is told. Deliberately phase labels plus an
 * elapsed clock and nothing finer: `artist_id_auto_collection_job` is one Dagster asset job
 * with no sensor-chained lineage, so there are no honest intermediate steps to report — and
 * a fake progress bar would restart the same trust problem this fixes.
 */
export function collectionIndicator(
  status: CollectionStatus,
  options: {
    elapsed?: number | null;
    isStalled?: boolean;
    errorDetail?: string | null;
  } = {}
): CollectionIndicator {
  const { elapsed = null, isStalled = false, errorDetail = null } = options;
  const clock = formatElapsed(elapsed);
  const withClock = (label: string) =>
    clock ? `${label}… ${clock}` : `${label}…`;

  if (isStalled && isActiveCollectionStatus(status)) {
    return {
      label: 'Taking longer than expected',
      detail: withClock('Taking longer than expected'),
      busy: true,
      attention: true,
    };
  }

  switch (status) {
    case 'triggering':
      return {
        label: 'Starting',
        detail: 'Starting…',
        busy: true,
        attention: false,
      };
    case 'queued':
      return {
        label: 'Queued',
        detail: withClock('Queued'),
        busy: true,
        attention: false,
      };
    case 'starting':
      return {
        label: 'Starting',
        detail: withClock('Starting'),
        busy: true,
        attention: false,
      };
    case 'collecting':
      return {
        label: 'Collecting IDs',
        detail: withClock('Collecting IDs'),
        busy: true,
        attention: false,
      };
    case 'cancelling':
      return {
        label: 'Cancelling',
        detail: withClock('Cancelling'),
        busy: true,
        attention: false,
      };
    case 'cancelled':
      return {
        label: 'Cancelled',
        detail: 'Cancelled',
        busy: false,
        attention: false,
      };
    case 'loaded':
      return {
        label: 'IDs collected',
        detail: 'IDs collected',
        busy: false,
        attention: false,
      };
    case 'failed':
      return {
        label: 'Failed — retry',
        detail: errorDetail ? `Failed — ${errorDetail}` : 'Failed — retry',
        busy: false,
        attention: true,
      };
    case 'unknown':
      return {
        label: 'Checking status',
        detail: 'Checking status…',
        busy: true,
        attention: true,
      };
    case 'idle':
    default:
      return {
        label: 'Not started',
        detail: 'Not started',
        busy: false,
        attention: false,
      };
  }
}
